import React, { useEffect, useState, useCallback } from "react";
import {
  X, Sparkles, FileText, Database, Layers, ShieldAlert, CheckCircle2, Loader2,
  ChevronDown, ChevronRight, RefreshCw, Info, AlertCircle, Folder, Eye,
} from "lucide-react";
import { Tile, Badge } from "./Primitives";
import {
  listObjects, inspectSchema, sampleBytes, stageBronze, listBronze,
} from "../lib/s3Api";
import { useDiscovery } from "../lib/discovery.jsx";
import { formatNumber, cn } from "../lib/utils";

// Discovery drawer for a real S3 connection. Fires three network calls per
// object in sequence: (1) list under the approved prefix, (2) inspect each
// selected object's schema, (3) optionally sample a byte-capped preview.
// The stage action calls POST /api/s3/bronze which persists to MongoDB.

const KNOWN_SUBJECT_LABELS = {
  pos: "POS Transactions",
  inventory: "Inventory Snapshots",
  supplier: "Supplier / PO",
  omnichannel: "Omnichannel Orders",
  masters: "Dimension Masters",
  external: "External Feeds",
};

export default function SchemaDiscoveryDrawer({ open, connection, onClose }) {
  const { addStaged } = useDiscovery();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [schemas, setSchemas] = useState({});
  const [samples, setSamples] = useState({});
  const [staged, setStaged] = useState({}); // key -> asset
  const [bronzeList, setBronzeList] = useState([]);

  const refresh = useCallback(async () => {
    if (!connection) return;
    setLoading(true);
    setError(null);
    try {
      const res = await listObjects(connection.id, { limit: 200 });
      setListing(res);
    } catch (e) {
      setError(e.message);
      setListing(null);
    } finally {
      setLoading(false);
    }
  }, [connection]);

  useEffect(() => {
    if (!open || !connection) return;
    setListing(null);
    setError(null);
    setSchemas({});
    setSamples({});
    setStaged({});
    setExpanded({});
    refresh();
    listBronze(connection.id).then(r => setBronzeList(r.assets || [])).catch(() => setBronzeList([]));
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, connection?.id]);

  if (!open || !connection) return null;

  const alreadyStaged = new Set(bronzeList.map(a => a.key));
  const objects = listing?.objects || [];
  const readable = objects.filter(o => ["parquet", "csv", "csv.gz"].includes(o.format));

  async function toggle(key) {
    setExpanded(e => ({ ...e, [key]: !e[key] }));
    if (!schemas[key]) {
      setSchemas(s => ({ ...s, [key]: { loading: true } }));
      try {
        const sc = await inspectSchema(connection.id, key);
        setSchemas(s => ({ ...s, [key]: { data: sc } }));
      } catch (e) {
        setSchemas(s => ({ ...s, [key]: { error: e.message } }));
      }
    }
  }

  async function preview(key) {
    setSamples(s => ({ ...s, [key]: { loading: true } }));
    try {
      const res = await sampleBytes(connection.id, key, 32768);
      // decode base64 → try UTF-8 (CSV) or hex-preview (parquet)
      const bin = atob(res.content_base64);
      let text;
      if (/\.csv(\.gz)?$/i.test(key)) {
        try {
          const bytes = new Uint8Array(bin.length);
          for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
          text = new TextDecoder("utf-8", { fatal: false }).decode(bytes);
        } catch {
          text = "(binary)";
        }
      } else {
        // Parquet → show first bytes as hex
        text = bin.slice(0, 256).split("").map(c => c.charCodeAt(0).toString(16).padStart(2, "0")).join(" ") + "\n… (binary Parquet)";
      }
      setSamples(s => ({ ...s, [key]: { data: text.slice(0, 4000) } }));
    } catch (e) {
      setSamples(s => ({ ...s, [key]: { error: e.message } }));
    }
  }

  async function stage(obj) {
    const sc = schemas[obj.key]?.data;
    if (!sc) return;
    const suggestedTable = suggestBronzeTable(connection, obj.key);
    const category = classifyCategory(connection.approved_prefix, obj.key);
    const primaryKey = suggestPrimaryKey(sc.columns);
    try {
      const asset = await stageBronze({
        connection_id: connection.id,
        connection_name: connection.name,
        bucket: connection.bucket,
        key: obj.key,
        format: obj.format,
        size: obj.size,
        rows: sc.rows ?? null,
        row_groups: sc.row_groups ?? null,
        partition: obj.partition || null,
        columns: sc.columns || [],
        suggested_table: suggestedTable,
        primary_key: primaryKey,
        category,
      });
      setStaged(s => ({ ...s, [obj.key]: asset }));
      setBronzeList(prev => [asset, ...prev.filter(a => a.key !== obj.key)]);
      addStaged(asset);
    } catch (e) {
      setSamples(s => ({ ...s, [obj.key]: { error: `Stage failed: ${e.message}` } }));
    }
  }

  // Layout compliance heuristics
  const layoutCompliant = objects.some(o => KNOWN_SUBJECT_LABELS[firstSegment(connection.approved_prefix, o.key)]);
  const hasMeta = objects.some(o => o.key.includes("/_meta/"));
  const smallFiles = objects.filter(o => ["parquet", "csv"].includes(o.format) && o.size > 0 && o.size < 8_000_000).length;
  const compactionCandidate = smallFiles > 100;

  return (
    <>
      <div data-testid="discovery-backdrop" onClick={onClose}
           className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 animate-fade-in" />
      <aside data-testid="discovery-drawer"
             className="fixed top-0 right-0 h-screen w-full sm:w-[600px] lg:w-[760px] xl:w-[860px] bg-bg border-l border-border shadow-2xl z-50 overflow-y-auto animate-slide-in-right">
        {/* Header */}
        <div className="sticky top-0 bg-bg/95 backdrop-blur-md border-b border-border px-5 py-3.5 flex items-start justify-between gap-4 z-10">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] text-primary font-semibold mb-1 inline-flex items-center gap-1.5">
              <Sparkles size={11} /> Schema Discovery · Live S3
            </div>
            <h2 className="font-display text-lg font-semibold tracking-tight leading-tight">{connection.name}</h2>
            <p className="text-[12px] text-muted mt-0.5 font-mono-tabular truncate">s3://{connection.bucket}/{connection.approved_prefix}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button data-testid="discovery-refresh" onClick={refresh}
                    className="w-8 h-8 inline-flex items-center justify-center border border-border hover:border-primary hover:text-primary rounded-sm">
              <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
            </button>
            <button data-testid="discovery-close" onClick={onClose}
                    className="w-8 h-8 inline-flex items-center justify-center border border-border hover:border-primary hover:text-primary rounded-sm">
              <X size={14} />
            </button>
          </div>
        </div>

        <div className="p-5 space-y-4">
          {/* Aggregate summary */}
          <Tile testid="discovery-summary" title="Approved folder summary"
                action={
                  <Badge status={connection.status === "connected" ? "success" : connection.status === "failing" ? "critical" : "info"}>
                    {connection.status || "draft"}
                  </Badge>
                }>
            {error && (
              <div data-testid="discovery-error" className="p-3 border border-critical/40 bg-critical/5 rounded-sm text-[12px] text-critical mb-3 inline-flex items-start gap-2">
                <AlertCircle size={13} className="mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <div className="font-semibold mb-0.5">AWS listing failed</div>
                  <div className="leading-snug break-words">{error}</div>
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-border tile !p-0 mb-3">
              <MiniStat label="Objects" value={listing ? objects.length : "…"} />
              <MiniStat label="Storage" value={listing ? `${((listing.total_bytes || 0) / 1_048_576).toFixed(1)} MB` : "…"} />
              <MiniStat label="dt= partitions" value={listing?.dt_partitions?.length ?? "…"} />
              <MiniStat label="Readable" value={listing ? readable.length : "…"}
                        status={listing && readable.length === 0 ? "warning" : "success"} />
            </div>

            {/* Formats */}
            {listing && Object.keys(listing.formats || {}).length > 0 && (
              <div className="flex flex-wrap gap-1.5" data-testid="discovery-formats">
                {Object.entries(listing.formats).map(([f, n]) => (
                  <Badge key={f} status={["parquet", "csv", "csv.gz"].includes(f) ? "info" : f === "meta" ? "success" : "neutral"}>
                    {f} · {n}
                  </Badge>
                ))}
              </div>
            )}
          </Tile>

          {/* Layout compliance panel */}
          <Tile testid="discovery-layout" title="Recommended layout · compliance"
                action={<Badge status={layoutCompliant && !compactionCandidate ? "success" : "warning"}>
                  {layoutCompliant && !compactionCandidate ? "Green" : "Amber"}
                </Badge>}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-[11.5px]" data-testid="discovery-layout-grid">
              <ComplianceItem
                label="Subject area recognised"
                ok={layoutCompliant}
                message={layoutCompliant ? "Recognised subject-area segment under the approved prefix" : "Approved prefix does not sit under a recognised subject area (pos/, inventory/, …). Falls back to generic Parquet/CSV inspection."} />
              <ComplianceItem
                label="_meta sidecars"
                ok={hasMeta}
                message={hasMeta ? "_meta/ sidecars detected — will use authoritative schema" : "No _meta/ sidecars found. Schema will be inferred from Parquet footers and CSV byte-range sniffs."} />
              <ComplianceItem
                label="File compaction"
                ok={!compactionCandidate}
                message={compactionCandidate ? `${smallFiles} files under 8 MB — recommend compaction to 64–512 MB per part-file for query performance.` : "File sizes look healthy for query performance."} />
            </div>
            {connection.layout_hint && (
              <div className="mt-3 text-[11px] text-muted flex items-center gap-2">
                <Folder size={12} className="text-primary" />
                Inferred subject area: <span className="font-mono-tabular text-fg">{connection.layout_hint}</span>
              </div>
            )}
          </Tile>

          {/* Object listing */}
          <Tile testid="discovery-objects" title={`Objects · ${objects.length} shown${objects.length === 200 ? " (truncated)" : ""}`}>
            {loading && !listing && (
              <div className="p-6 text-center text-muted text-[12px] inline-flex items-center gap-2 justify-center w-full">
                <Loader2 size={13} className="animate-spin" /> Listing under approved prefix…
              </div>
            )}
            {listing && objects.length === 0 && !error && (
              <div className="p-6 text-center text-muted text-[12px]">
                <Info size={16} className="text-info mx-auto mb-2" />
                The approved prefix returned zero objects. Verify the customer has populated data and the IAM policy allows <code className="font-mono-tabular">s3:ListBucket</code> on this prefix.
              </div>
            )}
            <div className="space-y-2">
              {objects.map((o) => {
                const isOpen = expanded[o.key];
                const isReadable = ["parquet", "csv", "csv.gz"].includes(o.format);
                const isStaged = alreadyStaged.has(o.key) || !!staged[o.key];
                return (
                  <div key={o.key} data-testid={`discovery-obj-${sanitize(o.key)}`}
                       className={cn("border rounded-sm transition-colors",
                         isStaged ? "border-success/40 bg-success/5" : "border-border bg-surface-2/40")}>
                    <div className="p-3 flex items-start gap-3">
                      <button
                        data-testid={`discovery-expand-${sanitize(o.key)}`}
                        onClick={() => isReadable && toggle(o.key)}
                        disabled={!isReadable}
                        className={cn("w-6 h-6 shrink-0 flex items-center justify-center rounded-sm",
                          isReadable ? "hover:bg-surface" : "opacity-30 cursor-not-allowed")}>
                        {isOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                      </button>
                      <div className={cn("w-8 h-8 rounded-sm flex items-center justify-center shrink-0",
                        o.format === "parquet" ? "bg-primary/15 text-primary"
                        : o.format === "csv" || o.format === "csv.gz" ? "bg-info/15 text-info"
                        : "bg-warning/15 text-warning")}>
                        <FileText size={14} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono-tabular text-[12px] truncate">{shortKey(o.key, connection.approved_prefix)}</span>
                          <Badge status={isReadable ? "info" : "neutral"}>{o.format}</Badge>
                          {o.partition && <Badge status="info">{o.partition}</Badge>}
                          {isStaged && <Badge status="success"><CheckCircle2 size={10} className="inline mr-1" />staged</Badge>}
                        </div>
                        <div className="flex items-center gap-3 text-[10px] text-muted mt-1 flex-wrap font-mono-tabular">
                          <span>{formatNumber(o.size, { compact: true })} B</span>
                          {o.last_modified && <span>· mod {o.last_modified.slice(0, 10)}</span>}
                          {!isReadable && <span className="text-warning">· format not supported</span>}
                        </div>
                      </div>
                      {isReadable && !isStaged && (
                        <button
                          data-testid={`discovery-stage-${sanitize(o.key)}`}
                          onClick={() => stage(o)}
                          disabled={!schemas[o.key]?.data}
                          className={cn("shrink-0 px-3 py-1.5 text-[11px] font-semibold rounded-sm border inline-flex items-center gap-1 transition-colors",
                            schemas[o.key]?.data
                              ? "border-primary text-primary hover:bg-primary hover:text-primary-fg"
                              : "border-border text-muted cursor-not-allowed")}>
                          <Layers size={11} /> Stage as Bronze
                        </button>
                      )}
                    </div>

                    {isOpen && isReadable && (
                      <div className="border-t border-border px-3 py-2.5 bg-bg space-y-2.5" data-testid={`discovery-schema-${sanitize(o.key)}`}>
                        <SchemaPanel state={schemas[o.key]} />
                        <div className="flex items-center gap-2">
                          <button data-testid={`discovery-sample-${sanitize(o.key)}`} onClick={() => preview(o.key)}
                                  className="px-2.5 py-1 text-[10.5px] border border-border hover:border-primary hover:text-primary rounded-sm inline-flex items-center gap-1">
                            <Eye size={10} /> Preview sample (≤32 KB)
                          </button>
                          <div className="text-[10px] text-muted">Reads a byte-capped range; large files stay in S3.</div>
                        </div>
                        <SamplePanel state={samples[o.key]} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </Tile>

          <div className="text-[11px] text-muted flex items-center gap-2">
            <Database size={12} className="text-primary" />
            Staged files land in the <span className="text-primary font-medium">Data Management Console → Bronze</span> as review items — the IT Head or COO cleans and promotes them to Silver there.
          </div>
        </div>
      </aside>
    </>
  );
}

function SchemaPanel({ state }) {
  if (!state) return null;
  if (state.loading) return <div className="text-[11px] text-muted inline-flex items-center gap-1.5"><Loader2 size={11} className="animate-spin" /> Inspecting schema…</div>;
  if (state.error) return <div className="text-[11px] text-critical inline-flex items-center gap-1.5"><AlertCircle size={11} /> {state.error}</div>;
  const sc = state.data;
  if (!sc) return null;
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-muted font-semibold mb-1.5 inline-flex items-center gap-2 flex-wrap">
        Inferred schema · {sc.columns?.length ?? 0} columns
        {sc.rows !== null && sc.rows !== undefined && <Badge status="info">{formatNumber(sc.rows, { compact: true })} rows</Badge>}
        {sc.row_groups && <Badge status="info">{sc.row_groups} row groups</Badge>}
        <Badge status="neutral">{sc.format}</Badge>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[11px]">
          <thead>
            <tr className="text-[10px] uppercase tracking-widest text-muted">
              <th className="py-1.5 px-2 text-left border-b border-border">Column</th>
              <th className="py-1.5 px-2 text-left border-b border-border">Type</th>
              <th className="py-1.5 px-2 text-left border-b border-border">Nullable</th>
              <th className="py-1.5 px-2 text-left border-b border-border">PII</th>
            </tr>
          </thead>
          <tbody className="font-mono-tabular">
            {(sc.columns || []).map((col, i) => (
              <tr key={i} className="border-b border-border last:border-b-0">
                <td className="py-1 px-2 font-medium">{col.name}</td>
                <td className="py-1 px-2 text-primary">{col.type}</td>
                <td className="py-1 px-2 text-muted">{col.nullable ? "Yes" : "No"}</td>
                <td className="py-1 px-2">
                  {col.pii
                    ? <span className="inline-flex items-center gap-1 text-warning"><ShieldAlert size={10} /> PII</span>
                    : <span className="text-muted">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SamplePanel({ state }) {
  if (!state) return null;
  if (state.loading) return <div className="text-[11px] text-muted inline-flex items-center gap-1.5"><Loader2 size={11} className="animate-spin" /> Streaming byte-capped sample…</div>;
  if (state.error) return <div className="text-[11px] text-critical inline-flex items-center gap-1.5"><AlertCircle size={11} /> {state.error}</div>;
  if (!state.data) return null;
  return (
    <pre className="text-[10.5px] font-mono-tabular leading-snug bg-surface-2/60 border border-border rounded-sm p-2 overflow-x-auto whitespace-pre max-h-[220px]">{state.data}</pre>
  );
}

function ComplianceItem({ label, ok, message }) {
  return (
    <div className={cn("p-2.5 border rounded-sm",
      ok ? "border-success/30 bg-success/5" : "border-warning/30 bg-warning/5")}>
      <div className="flex items-center gap-1.5 mb-1">
        {ok ? <CheckCircle2 size={11} className="text-success" /> : <AlertCircle size={11} className="text-warning" />}
        <span className={cn("text-[11px] font-semibold uppercase tracking-widest", ok ? "text-success" : "text-warning")}>{label}</span>
      </div>
      <div className="text-[10.5px] text-fg/90 leading-snug">{message}</div>
    </div>
  );
}

function MiniStat({ label, value, status }) {
  return (
    <div className="px-4 py-2.5">
      <div className={cn("text-[9px] uppercase tracking-widest font-semibold", status === "warning" ? "text-warning" : status === "success" ? "text-success" : "text-muted")}>{label}</div>
      <div className="text-lg font-display font-semibold mt-0.5">{value}</div>
    </div>
  );
}

function sanitize(k) {
  return k.replace(/[^a-zA-Z0-9]+/g, "-").slice(0, 60);
}

function shortKey(k, approved) {
  if (approved && k.startsWith(approved)) return k.slice(approved.length) || "(root)";
  return k;
}

function firstSegment(approved, key) {
  const rel = key.startsWith(approved) ? key.slice(approved.length) : key;
  return (rel.split("/")[0] || "").toLowerCase();
}

// Suggest a Bronze table name from the object key (or the approved prefix path).
function suggestBronzeTable(connection, key) {
  const rel = key.startsWith(connection.approved_prefix) ? key.slice(connection.approved_prefix.length) : key;
  const [subject = "", table = ""] = rel.split("/");
  const clean = (v) => (v || "").replace(/[^a-z0-9_]+/g, "_").replace(/^_+|_+$/g, "").toLowerCase();
  const subj = clean(subject) || connection.bucket.replace(/[^a-z0-9]+/g, "_");
  const tbl = clean(table) || "raw";
  return `bronze_${subj}_${tbl}`.slice(0, 80);
}

function classifyCategory(approved, key) {
  const seg = firstSegment(approved, key);
  return KNOWN_SUBJECT_LABELS[seg] || "Uncategorised";
}

function suggestPrimaryKey(columns) {
  if (!columns || columns.length === 0) return null;
  const names = columns.map(c => c.name.toLowerCase());
  const pref = ["id", "tx_id", "po_id", "snapshot_ts+branch_id+sku_id"];
  for (const p of pref) {
    if (p.includes("+")) {
      const parts = p.split("+");
      if (parts.every(x => names.includes(x))) return p;
    } else if (names.includes(p)) return p;
  }
  if (names.includes("snapshot_ts") && names.includes("branch_id") && names.includes("sku_id")) return "snapshot_ts+branch_id+sku_id";
  if (names.includes("tx_ts") && names.includes("branch_id") && names.includes("sku_id")) return "tx_ts+branch_id+sku_id";
  return columns[0]?.name || null;
}
